import React from 'react';
import { Order } from '../../pages/Orders';
import { MenuIcon, BellIcon, PackageIcon, TruckIcon, CalendarIcon, ClockIcon, CreditCardIcon, AlertTriangleIcon } from 'lucide-react';
interface OrderCardProps {
  order: Order;
  compact?: boolean;
}
export function OrderCard({
  order,
  compact = false
}: OrderCardProps) {
  // Format price with + for positive values
  const formatPrice = (price: number) => {
    return price >= 0 ? `+ $${price.toFixed(2)}` : `$${Math.abs(price).toFixed(2)}`;
  };
  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'text-green-600';
      case 'cancelled':
        return 'text-red-600';
      case 'unpaid':
        return 'text-amber-600';
      default:
        return 'text-gray-600';
    }
  };
  // Get order status badge
  const getOrderStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800" data-id="element-1189">
            New
          </span>;
      case 'in-progress':
        return <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-800" data-id="element-1190">
            In Progress
          </span>;
      case 'completed':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800" data-id="element-1191">
            Completed
          </span>;
      case 'cancelled':
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800" data-id="element-1192">
            Cancelled
          </span>;
      case 'open':
        return <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800" data-id="element-1193">
            Open
          </span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800" data-id="element-1194">
            {status}
          </span>;
    }
  };
  // Get shipping status icon
  const getShippingStatusIcon = (status: string) => {
    switch (status) {
      case 'new':
        return <PackageIcon size={16} className="text-blue-600 mr-1" data-id="element-1195" />;
      case 'in-progress':
        return <TruckIcon size={16} className="text-amber-600 mr-1" data-id="element-1196" />;
      case 'completed':
        return <CheckIcon size={16} className="text-green-600 mr-1" data-id="element-1197" />;
      default:
        return null;
    }
  };
  return <div className={`bg-white p-4 ${compact ? 'shadow-sm border border-gray-200 rounded-lg' : ''}`} data-id="element-1198">
      <div className="flex justify-between items-start mb-3" data-id="element-1199">
        <div data-id="element-1200">
          <div className="flex items-center" data-id="element-1201">
            <span className="text-gray-800 font-medium" data-id="element-1202">#{order.id}</span>
            <span className={`ml-2 ${getStatusColor(order.paymentStatus)}`} data-id="element-1203">
              {order.paymentStatus.charAt(0).toUpperCase() + order.paymentStatus.slice(1)}
            </span>
            <span className="ml-2" data-id="element-1204">{getOrderStatusBadge(order.status)}</span>
          </div>
          <div className="flex items-center text-xs text-gray-500 mt-1" data-id="element-1205">
            <CalendarIcon size={14} className="mr-1" data-id="element-1206" />
            <span data-id="element-1207">{order.date}</span>
            <ClockIcon size={14} className="ml-2 mr-1" data-id="element-1208" />
            <span data-id="element-1209">{order.time}</span>
          </div>
        </div>
        <div className="flex items-center" data-id="element-1210">
          {order.paymentStatus === 'cancelled' && <div className="mr-2 text-red-500" data-id="element-1211">
              <AlertTriangleIcon size={18} data-id="element-1212" />
            </div>}
          <MenuIcon size={18} className="text-gray-400" data-id="element-1213" />
        </div>
      </div>
      {/* Customer info */}
      <div className="mb-3 text-sm" data-id="element-1214">
        <div className="flex items-start" data-id="element-1215">
          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 mr-2 flex-shrink-0" data-id="element-1216">
            {order.customer.name.charAt(0).toUpperCase()}
          </div>
          <div data-id="element-1217">
            <div className="font-medium" data-id="element-1218">{order.customer.name}</div>
            <div className="text-xs text-gray-500" data-id="element-1219">{order.customer.email}</div>
            <div className="text-xs text-gray-500" data-id="element-1220">
              {order.customer.mobileNumber}
            </div>
            {!compact && <div className="text-xs text-gray-500 mt-1" data-id="element-1221">
                {order.customer.address}
              </div>}
          </div>
        </div>
      </div>
      {/* Order items */}
      <div className="border-t border-gray-100 pt-2 mb-2" data-id="element-1222">
        {order.items.map((item, index) => <div key={index} className="flex justify-between items-center py-1" data-id="element-1223">
            <div className="text-sm" data-id="element-1224">
              {item.quantity} x {item.name}
            </div>
            <div className={`text-sm font-medium ${order.paymentStatus === 'cancelled' ? 'text-gray-700' : 'text-green-600'}`} data-id="element-1225">
              {formatPrice(item.price * item.quantity)}
            </div>
          </div>)}
      </div>
      {/* Order total */}
      <div className="flex justify-between items-center border-t border-gray-100 pt-2" data-id="element-1226">
        <div className="flex items-center" data-id="element-1227">
          <CreditCardIcon size={16} className="text-gray-500 mr-1" data-id="element-1228" />
          <span className="text-xs text-gray-500" data-id="element-1229">
            {order.transactionStatus ? `Transaction ${order.transactionStatus}` : 'No transaction info'}
          </span>
        </div>
        <div className="font-bold text-gray-900" data-id="element-1230">${order.total.toFixed(2)}</div>
      </div>
    </div>;
}
// Missing icon
const CheckIcon = ({
  size = 24,
  ...props
}) => <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props} data-id="element-1231">
    <polyline points="20 6 9 17 4 12" data-id="element-1232" />
  </svg>;